// src/App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { WISHLIST_ENABLED } from './config/features';
import { useDispatch } from 'react-redux';  // <-- FIXED: Import useDispatch
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { store } from './store';
import { fetchCategories } from './store/slices/categorySlice';
import { fetchSettings } from './store/slices/settingSlice';
import { initializeCart } from './store/slices/cartSlice';
import { captureUtmParams } from './utils/utm';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './context/ToastContext';
import { PrivateRoute } from './auth/PrivateRoute';
import SiteFavicon from './components/common/SiteFavicon';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import BottomFooter from './components/layout/BottomFooter';
import "./style/style.css";
// Swiper CSS is imported in ProductDetailPage.js — only loads when needed
import ScrollToTop from './components/common/ScrollToTop';
import ChatWidget from './components/widgets/ChatWidget';
import CookieConsentBanner from './components/common/CookieConsentBanner';
// Eager (not lazy): "/" is the most-visited route and the hero's LCP shouldn't
// wait on an extra JS-chunk fetch on top of the API round-trip it already needs.
import HomePage from './pages/HomePage';

const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const CategoryProductsPage = lazy(() => import('./pages/CategoryProductsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderCompletePage = lazy(() => import('./pages/OrderCompletePage'));
const PaymentFailedPage = lazy(() => import('./pages/PaymentFailedPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const ProfileOrdersPage = lazy(() => import('./pages/ProfileOrdersPage'));
const ProfileOrderDetailPage = lazy(() => import('./pages/ProfileOrderDetailPage'));
const AddressPage = lazy(() => import('./pages/AddressPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const TrackOrdersPage = lazy(() => import('./pages/TrackOrdersPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const CareerDetailPage = lazy(() => import('./pages/CareerDetailPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const CMSPage = lazy(() => import('./pages/CMSPage'));
const UseCaseDetailPage = lazy(() => import('./pages/UseCaseDetailPage'));
const UseCaseTemplatePage = lazy(() => import('./pages/UseCaseTemplatePage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const CaseStudyDetailPage = lazy(() => import('./pages/CaseStudyDetailPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const NewslettersPage = lazy(() => import('./pages/NewslettersPage'));
const SpotlightsPage = lazy(() => import('./pages/SpotlightsPage'));
const SpotlightMicrositePage = lazy(() => import('./pages/SpotlightMicrositePage'));
const InvestorsPage = lazy(() => import('./pages/InvestorsPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const ProductEnquiryReceivedPage = lazy(() => import('./pages/ProductEnquiryReceivedPage'));

const LoadingScreen = () => (
  <Box
    component="section"
    sx={{
      minHeight: '80vh', // Increased minHeight to reduce CLS when content loads
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'background.default',
    }}
  >
    <CircularProgress size={48} />
  </Box>
);

// Child component inside Provider
const AppContent = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    captureUtmParams();
    dispatch(fetchCategories());
    dispatch(initializeCart());
    dispatch(fetchSettings());

    // Remove the snap fallback style to reveal the loaded page
    const fallbackStyle = document.getElementById('snap-fallback-style');
    if (fallbackStyle) {
      fallbackStyle.remove();
    }
  }, [dispatch]);

  return (
    <ToastProvider>
      <SiteFavicon />
      <Router basename={process.env.PUBLIC_URL || ''}>
        <ScrollToTop />
        <div className="App" >
          <div style={{ position: 'sticky', top: 0, zIndex: 1200 }}>
            <Header />
          </div>
          <Box
            component="main"
            id="main-content"
          >
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<CategoriesPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/categories/:id" element={<CategoriesPage />} />
                {/* Legacy product-suffix routes kept for backward compat */}
                <Route path="/categories/:id/products" element={<CategoryProductsPage />} />
                <Route path="/categories/:l1/:l2/products" element={<CategoryProductsPage />} />
                <Route path="/categories/:l1/:l2/:l3/products" element={<CategoryProductsPage />} />
                {/* Hierarchical category pages: /products/l1/l2 or /products/l1/l2/l3 */}
                <Route path="/products/:l1/:l2" element={<CategoriesPage />} />
                <Route path="/products/:l1/:l2/:l3" element={<CategoriesPage />} />
                {/* Nested /categories/* → subcategory page */}
                <Route path="/categories/*" element={<CategoriesPage />} />
                {/* Bare /industries → CMS page (content managed in backend) */}
                <Route path="/industries" element={<CMSPage />} />
                {/* Industries — wildcard catches /industries/l1 and /industries/l1/l2.
                    CategoriesPage detects parent vs leaf: parent shows subcategory grid,
                    leaf renders CategoryProductsPage inline. */}
                <Route path="/industries/*" element={<CategoriesPage />} />
                {/* Single-segment /products/:id is ambiguous: it's either a
                    top-level category (e.g. /products/serial-device-servers)
                    or a flat product slug — CategoriesPage resolves which by
                    checking the category list first, falling back to
                    ProductDetailPage when no category matches (see its
                    isProduct/isLeaf branching). */}
                <Route path="/products/:id" element={<CategoriesPage />} />
                {/* Hierarchical product URLs: /products/{l1}/{l2}/{l3}/{productSlug} (4+ segments).
                    Less specific than the fixed-segment routes above, so it only matches
                    paths not already caught by /products/:l1/:l2/:l3. ProductDetailPage
                    extracts the last segment as the product ID. */}
                <Route path="/products/*" element={<ProductDetailPage />} />
                <Route path="/category/:slug?" element={<CategoryPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
                <Route path="/order-complete" element={<PrivateRoute><OrderCompletePage /></PrivateRoute>} />
                <Route path="/payment-failed" element={<PaymentFailedPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                <Route path="/profile/edit" element={<PrivateRoute><EditProfilePage /></PrivateRoute>} />
                <Route path="/profile/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
                <Route path="/profile/address" element={<PrivateRoute><AddressPage /></PrivateRoute>} />
                <Route path="/profile/orders" element={<PrivateRoute><ProfileOrdersPage /></PrivateRoute>} />
                <Route path="/profile/order/:id" element={<PrivateRoute><ProfileOrderDetailPage /></PrivateRoute>} />
                {WISHLIST_ENABLED && <Route path="/wishlist" element={<WishlistPage />} />}
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/track-orders" element={<PrivateRoute><TrackOrdersPage /></PrivateRoute>} />
                <Route path="/contact-us" element={<ContactPage />} />
                <Route path="/about-us" element={<AboutPage />} />
                <Route path="/faqs" element={<FAQPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/careers/:slug" element={<CareerDetailPage />} />
                <Route path="/industries/:slug" element={<UseCaseDetailPage />} />
                <Route path="/industries/:category/:slug" element={<UseCaseTemplatePage />} />
                <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/newsletters" element={<NewslettersPage />} />
                {/* /spotlights and /spotlight used to render the same page at two
                    URLs — a straight duplicate-content pair with no canonical
                    resolution between them (both indexable, sitemap listed both).
                    /spotlight is the real one (it's the base of /spotlight/:slug
                    microsite pages too); /spotlights redirects to it. */}
                <Route path="/spotlights" element={<Navigate to="/spotlight/" replace />} />
                <Route path="/spotlight" element={<SpotlightsPage />} />
                <Route path="/spotlight/:slug" element={<SpotlightMicrositePage />} />
                <Route path="/investors" element={<InvestorsPage />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/event/:slug" element={<EventDetailPage />} />
                <Route path="/product-enquiry-received" element={<ProductEnquiryReceivedPage />} />
                <Route path="/:slug" element={<CMSPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Box>
          <BottomFooter />
          <ChatWidget />
          <CookieConsentBanner />
        </div>
      </Router>
    </ToastProvider>
  );
};

const baseTheme = createTheme();
const customShadows = [
  'none',
  '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.14)',
  '0px 3px 6px rgba(0, 0, 0, 0.15), 0px 2px 4px rgba(0, 0, 0, 0.12)',
  '0px 10px 20px rgba(0, 0, 0, 0.15), 0px 3px 6px rgba(0, 0, 0, 0.10)',
  '0px 15px 35px rgba(0, 0, 0, 0.1), 0px 5px 15px rgba(0, 0, 0, 0.07)',
  '0px 20px 40px rgba(0, 0, 0, 0.1), 0px 7px 20px rgba(0, 0, 0, 0.07)',
  '0px 25px 45px rgba(0, 0, 0, 0.1), 0px 9px 25px rgba(0, 0, 0, 0.07)',
  '0px 30px 50px rgba(0, 0, 0, 0.1), 0px 11px 30px rgba(0, 0, 0, 0.07)',
  '0px 35px 55px rgba(0, 0, 0, 0.1), 0px 13px 35px rgba(0, 0, 0, 0.07)',
  '0px 40px 60px rgba(0, 0, 0, 0.1), 0px 15px 40px rgba(0, 0, 0, 0.07)',
];

const theme = createTheme({
  palette: {
    primary: {
      main: '#178ACB',
      light: '#50aee0',
      dark: '#1270a8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5D5D5F', // Modern indigo
      light: '#221F20',
      dark: '#221F20',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#f59e0b', // Amber for highlights
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    surface: {
      main: '#f8fafc',
      variant: '#f1f5f9',
    },
    text: {
      primary: '#5D5D5F',
      secondary: '#178ACB',
      muted: '#D6914A',
    },
    error: {
      main: '#ee1111ff',
      light: '#fca5a5',
      dark: '#dc2626',
    },
    success: {
      main: '#1a8a16de',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    brand: {
      main: '#005DAA',    // Dynalog logo blue — buttons, CTAs
      light: '#178ACB',   // Teal/cyan accent — prices, links
      dark: '#004b8a',    // Hover / pressed state
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Manrope", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.3,

    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.3,
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    primary: {
      fontFamily: 'Parisienne',
      fontSize: '28px',
      lineHeight: 1.6,
      color: '#221F20',
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '22px',
      },
    },
    secondary: {
      fontFamily: '"Manrope", sans-serif', fontSize: '20px', lineHeight: 1.3
    },
    secondaryHeading: {
      fontFamily: '"Manrope", sans-serif', fontSize: '45px', fontWeight: 700,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '30px',
      },
    },
    secondaryHeading2: {
      fontFamily: '"Manrope", sans-serif', fontSize: '19px', fontWeight: 700
    },
    secondaryHeading3: {
      fontFamily: '"Manrope", sans-serif', fontSize: '17px', fontWeight: 400
    },
    primaryHeading: {
      fontFamily: 'Parisienne',
      fontSize: '28px',
      lineHeight: 1.3,
      color: '#178ACB',
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '22px',
      },
    },
    categoryTitle: {
      fontFamily: 'Parisienne', fontSize: '28px !important', lineHeight: 1.3, flexShrink: 0, fontWeight: 400, pb: "2px",
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '22px !important',
      },
    },
    exoticsTitle: {
      fontFamily: 'Parisienne', fontSize: '56px', lineHeight: 1.3, fontWeight: 400,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '44px !important',
      },
    },
    footerMenu: {
      fontFamily: '"Manrope", sans-serif', fontSize: '18px', lineHeight: 1.3, fontWeight: 400, color: '#ffffff !important', textDecoration: 'none !important'
    },
  },
  shape: {
    borderRadius: 2,
  },
  shadows: baseTheme.shadows.map((shadow, index) => customShadows[index] ?? shadow),
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          padding: '8px 16px',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          color: 'white',
          py: 1.5,
          borderRadius: 3,
          backgroundColor: '#178ACB',
          '&:hover': {
            backgroundColor: '#1270a8',
            transform: 'translateY(-1px)',
          },
          '&:disabled': {
            background: 'grey.300',
            color: 'grey.500',
            transform: 'none',
          },
          transition: 'all 0.3s ease',
        },
        outlinedPrimary: {
          color: '#178ACB',
          py: 1.5,
          borderRadius: 3,
          backgroundColor: 'transparent',
          border: '1px solid #178ACB',
          '&:hover': {
            backgroundColor: 'rgba(23, 138, 203, 0.06)',
            transform: 'translateY(-1px)',
          },
          '&:disabled': {
            background: 'grey.300',
            color: 'grey.500',
            transform: 'none',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: '#ffffff',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: '0 0 0 2px rgba(255, 63, 108, 0.08)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          fontWeight: 600,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1e293b',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiDivider: {
      variants: [
        {
          props: { variant: "categoryDivider" },
          style: {
            flex: 1,
            flexGrow: 1,
            marginLeft: 16,
            marginRight: 16,
            borderBottom: "2px solid transparent",
            borderImage: "repeating-linear-gradient(to right, currentColor 0 4px, transparent 4px 10px) 1",
          },
        },
      ],
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "footerContactHeading" },
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontSize: '50px',
            fontWeight: 700,
            lineHeight: 1.2,
            color: '#faf8f8',
          },
        },
        {
          props: { variant: "footerSectionHeading" },
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: 1.3,
            color: '#faf8f8',
          },
        },
        {
          props: { variant: "footerText" },
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.3,
            color: '#faf8f8',
          },
        },
        {
          props: { variant: "primary" },
          style: {
            fontFamily: 'Parisienne',
            fontSize: '28px',
            lineHeight: 1.3,
            [baseTheme.breakpoints.down('sm')]: {
              fontSize: '22px',
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontSize: '20px',
            lineHeight: 1.3,
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
        },
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;